import { useEffect, useState } from 'react'
import type { HistorySegment } from '@cht/shared'
import type { FieldClient, PropertyPerson } from '../core/api/field-client.js'
import { Button, InlineNotice } from './components.js'
import { db } from '../core/db.js'
import { HistoryCache } from '../core/history/history-cache.js'

export function HistoryPanel({ client, admin, requestedUserId, onSegments, onSelectUser }: {
  client: FieldClient
  admin: boolean
  requestedUserId?: string
  onSegments: (segments: HistorySegment[]) => void
  onSelectUser: (userId?: string) => void
}) {
  const today = toDateInput(new Date())
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [userId, setUserId] = useState(requestedUserId ?? 'all')
  const [people, setPeople] = useState<PropertyPerson[]>([])
  const [segments, setSegments] = useState<HistorySegment[]>([])
  const [selected, setSelected] = useState<HistorySegment>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [cachedAt, setCachedAt] = useState<number>()
  const cache = new HistoryCache(client.propertyScope(), db)

  useEffect(() => {
    if (admin) void client.people().then(setPeople).catch(() => setError('People could not be loaded for the history selector.'))
  }, [client, admin])
  useEffect(() => { void load(requestedUserId ?? 'all') }, [client, requestedUserId])
  useEffect(() => () => onSegments([]), [onSegments])

  async function load(selectedUserId = userId) {
    setUserId(selectedUserId)
    const from = new Date(`${fromDate}T00:00:00`).getTime()
    const to = new Date(`${toDate}T23:59:59.999`).getTime()
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) { setError('Choose a valid start and end date.'); return }
    if (to - from > 31 * 24 * 60 * 60 * 1000) { setError('History ranges can cover at most 31 days.'); return }
    setLoading(true); setError(undefined); setCachedAt(undefined); setSelected(undefined)
    try {
      const scopedUserId = admin && selectedUserId !== 'all' ? selectedUserId : undefined
      const loaded = await client.history(from, to, scopedUserId)
      setSegments(loaded); onSegments(loaded); onSelectUser(scopedUserId)
      await cache.write(from, to, loaded, scopedUserId).catch(() => undefined)
    } catch {
      const scopedUserId = admin && selectedUserId !== 'all' ? selectedUserId : undefined
      const cached = await cache.read(from, to, scopedUserId)
      if (cached) { setSegments(cached.segments); onSegments(cached.segments); onSelectUser(scopedUserId); setCachedAt(cached.cachedAt) }
      else setError('History could not be loaded and this device has no saved copy for the selected range.')
    }
    finally { setLoading(false) }
  }

  if (selected) return <div className="history-panel history-detail">
    <Button type="button" variant="ghost" onClick={() => setSelected(undefined)}>Back to history</Button>
    <p className="data">{selected.displayName.toUpperCase()} · {new Date(selected.startedAt).toLocaleDateString()}</p>
    <h2>{timeRange(selected)}</h2>
    <div className="history-metrics"><span><strong>{formatDistance(selected.distanceMeters)}</strong> distance</span><span><strong>{formatDuration(selected.endedAt - selected.startedAt)}</strong> duration</span><span><strong>{accuracySummary(selected)}</strong> accuracy</span><span><strong>{selected.fixCount}</strong> recorded fixes</span></div>
    <InlineNotice tone="info" title="Display line">The map may simplify the drawn line for readability. Distance and fix count come from all recorded fixes.</InlineNotice>
  </div>

  return <div className="history-panel">
    {admin && <label>Whose history<select value={userId} onChange={(event) => setUserId(event.target.value)}><option value="all">All people</option>{people.map((person) => <option key={person.id} value={person.id}>{person.displayName}</option>)}</select></label>}
    <div className="history-range"><label>From<input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></label><label>To<input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} /></label></div>
    <Button type="button" loading={loading} onClick={() => void load()}>Load history</Button>
    {error && <InlineNotice tone="fail">{error}</InlineNotice>}
    {cachedAt && <InlineNotice tone="pending" title="Saved history">Showing the last copy saved on this device at {new Date(cachedAt).toLocaleString()}. Reconnect to check for newer fixes.</InlineNotice>}
    {!loading && !error && segments.length === 0 ? <p className="field-message">No recorded fixes in this range.</p> : <div className="history-list">{segments.map((segment) => <button type="button" key={segment.id} onClick={() => { setSelected(segment); onSelectUser(segment.userId) }}>
      <span><strong>{timeRange(segment)}</strong><small>{segment.displayName}</small></span><span>{formatDistance(segment.distanceMeters)}<small>{segment.fixCount} fixes</small></span>
    </button>)}</div>}
    <p className="data">Segments start after a gap longer than 10 minutes or a fence crossing. They are not named outings.</p>
  </div>
}

function toDateInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
const timeRange = (segment: HistorySegment) => `${new Date(segment.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–${new Date(segment.endedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
const formatDistance = (meters: number) => meters >= 1_000 ? `${(meters / 1_000).toFixed(1)} km` : `${Math.round(meters)} m`
const formatDuration = (milliseconds: number) => {
  const minutes = Math.max(0, Math.round(milliseconds / 60_000))
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes} min`
}
const accuracySummary = (segment: HistorySegment) => {
  const accuracies = segment.fixes.map((fix) => fix.accuracyMeters).filter(Number.isFinite)
  if (!accuracies.length) return '—'
  return `±${Math.round(accuracies.reduce((sum, value) => sum + value, 0) / accuracies.length)} m avg`
}
